import { useContext } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { UnleashKeys } from '../../constants/unleash-keys';
import type { CdmOfflineConfiguration } from './cdm-offline-configuration';
import type { CdmOfflineConfigurationReadResult } from './cdm-read-offline-configuration';
import { UseSavedCdmConfigurationContext } from './cdm-use-saved-configuration.context';
import type { OfflineDataStoreIdentity } from './offline-data-store';

export const getSavedCdmConfigurationQueryKey = ({
  cluster,
  project,
  userId,
}: OfflineDataStoreIdentity) =>
  ['cdm-saved-configuration', cluster, project, userId] as const;

const getSavedCdmConfigurationQueryOptions = (
  identity: OfflineDataStoreIdentity,
  read: () => Promise<CdmOfflineConfigurationReadResult>
) =>
  queryOptions({
    queryKey: getSavedCdmConfigurationQueryKey(identity),
    networkMode: 'always',
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
    queryFn: async () => {
      const result = await read();
      if (result.status === 'unavailable') {
        throw new Error(`Saved CDM configuration is ${result.reason}`);
      }
      return result.configuration;
    },
  });

interface OnlineResult<T> {
  data?: T;
  isError: boolean;
  error?: Error | null;
  isLoading?: boolean;
}

export interface SavedCdmConfigurationResult<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Shares validated saved configuration across startup consumers without populating their online query caches.
 * While the `offline_multiple_checklists` flag is off, it returns only the online result.
 */
export const useSavedCdmConfiguration = <T>({
  useOnlineQuery,
  select,
  enabled = true,
}: {
  useOnlineQuery: (options: { enabled: boolean }) => OnlineResult<T>;
  select: (configuration: CdmOfflineConfiguration) => T;
  enabled?: boolean;
}): SavedCdmConfigurationResult<T> => {
  const {
    useAuthContext,
    useIsOnline,
    useFlag,
    readConfiguration,
    indexedDbFactory,
  } = useContext(UseSavedCdmConfigurationContext);
  const { authState, client } = useAuthContext();
  const isOnline = useIsOnline();
  const isFlagEnabled = useFlag(UnleashKeys.offline_multiple_checklists);
  const canUseSaved = enabled && isFlagEnabled;
  const online = useOnlineQuery({ enabled: enabled && isOnline });
  const identity = {
    cluster: client.getBaseUrl(),
    project: client.project,
    userId: authState.user.cdfId,
  };
  const saved = useQuery({
    ...getSavedCdmConfigurationQueryOptions(identity, () =>
      readConfiguration(identity, indexedDbFactory ?? indexedDB)
    ),
    enabled: canUseSaved && !isOnline && online.data === undefined,
    select,
  });

  // Restored data stays visible after reconnecting until online data arrives.
  // A failed online request does not replace it.
  const shouldReturnOnlineResult =
    !canUseSaved ||
    online.data !== undefined ||
    (isOnline && saved.data === undefined);
  return shouldReturnOnlineResult
    ? {
        data: online.data,
        isError: online.isError,
        error: online.error ?? null,
        isLoading:
          online.isLoading ??
          (enabled && online.data === undefined && !online.isError),
      }
    : {
        // TanStack Query keeps the previous selection when select throws.
        data: saved.isError ? undefined : saved.data,
        isLoading: saved.isPending,
        isError: saved.isError,
        error: saved.error,
      };
};
